/**
 * @component ry-button
 * @category  form
 * @summary   Light-DOM button with full Fomantic parity. Styling lives in CSS; this class wires ARIA, keyboard, focus, and the disabled/loading non-interactive contract.
 *
 * @attribute variant         string?   default=neutral   primary | secondary | basic | outline | ghost | danger | negative | positive | success | accent | link, plus 13 palette colors (red, orange, yellow, olive, green, teal, blue, violet, purple, pink, brown, grey, black).
 * @attribute size            string?   default=medium    mini | tiny | small | sm | medium | md | large | lg | big | huge | massive. Driven by font-size; em-based padding scales automatically.
 * @attribute disabled        boolean   default=false     Non-interactive. Mirrors aria-disabled, tabindex=-1, blocks click and keyboard activation.
 * @attribute loading         boolean   default=false     Async work in progress. Sets aria-busy, renders an overlay spinner, hides children, blocks interaction.
 * @attribute pressed         boolean   default=false     Sticky toggle state. Mirrors aria-pressed="true".
 * @attribute fluid           boolean   default=false     Stretches to full container width.
 * @attribute circular        boolean   default=false     Rounded into a pill. Intended for icon-only buttons.
 * @attribute icon            boolean   default=false     Icon-only mode — equal padding all sides. Requires aria-label.
 * @attribute icon-position   string?   default=left      left | right — which side an inline <ry-icon> child sits on.
 * @attribute attached        string?                     top | bottom | left | right — suppresses radius on that edge so the button hugs an adjacent surface (card, input, panel).
 * @attribute animated        string?                     horizontal (default) | vertical | fade — on hover, swaps a [data-content="visible"] block for a [data-content="hidden"] one.
 * @attribute modal           string?                     Declarative hook: ID of a <ry-modal> to open on click.
 * @attribute drawer          string?                     Declarative hook: ID of a <ry-drawer> to open on click.
 * @attribute aria-label      string?                     Required when icon-only. A console warning fires if absent.
 *
 * @property disabled  boolean  Mirrors the attribute (get/set).
 * @property pressed   boolean  Mirrors the attribute (get/set).
 * @property loading   boolean  Mirrors the attribute (get/set).
 *
 * @event ry:click
 *        detail: { originalEvent: MouseEvent }
 *        fires:  any genuine activation — mouse click, Space, Enter. Suppressed while [disabled] or [loading].
 *
 * @keyboard Enter, Space — activate. Space's default action (page scroll) is prevented.
 * @state    pressed — sticky on/off, mirrored to aria-pressed.
 * @state    loading — async, mirrored to aria-busy.
 *
 * @variation variant — 24 visual styles. Default is a neutral gray (Semantic UI parity).
 * @variation size — 8-step scale (mini → massive) plus sm/md/lg aliases.
 *
 * @content Any text or markup. For animated buttons, use two children:
 *          <span data-content="visible">Default</span>
 *          <span data-content="hidden">On hover</span>
 *
 * @gotcha  Icon-only buttons MUST have aria-label or aria-labelledby. The
 *          component logs a console warning at construction if missing.
 *
 * @gotcha  disabled and loading both block click + keyboard, but only
 *          disabled changes the visual styling to look "disabled". loading
 *          shows the spinner overlay while keeping the colored background.
 *
 * @example default
 * ```html
 * <ry-button>OK</ry-button>
 * ```
 *
 * @example primary
 * ```html
 * <ry-button variant="primary">Save</ry-button>
 * ```
 *
 * @example palette-color
 * ```html
 * <ry-button variant="teal">Teal</ry-button>
 * <ry-button variant="purple">Purple</ry-button>
 * ```
 *
 * @example sizes
 * ```html
 * <ry-button size="mini">mini</ry-button>
 * <ry-button size="small">small</ry-button>
 * <ry-button>default</ry-button>
 * <ry-button size="large">large</ry-button>
 * <ry-button size="massive">massive</ry-button>
 * ```
 *
 * @example icon-only
 * ```html
 * <ry-button icon aria-label="Add">
 *   <ry-icon name="plus"></ry-icon>
 * </ry-button>
 * ```
 *
 * @example icon-with-label
 * ```html
 * <ry-button>
 *   <ry-icon name="download"></ry-icon> Download
 * </ry-button>
 * <ry-button icon-position="right">
 *   Next <ry-icon name="chevron-right"></ry-icon>
 * </ry-button>
 * ```
 *
 * @example loading
 * ```html
 * <ry-button loading variant="primary">Saving…</ry-button>
 * ```
 *
 * @example disabled
 * ```html
 * <ry-button disabled>Can't click me</ry-button>
 * ```
 *
 * @example fluid
 * ```html
 * <ry-button fluid variant="primary">Full width</ry-button>
 * ```
 *
 * @example circular
 * ```html
 * <ry-button circular icon aria-label="Like">
 *   <ry-icon name="heart"></ry-icon>
 * </ry-button>
 * ```
 *
 * @example attached
 * ```html
 * <ry-card>
 *   <p>Some content.</p>
 *   <ry-button attached="bottom" variant="primary" fluid>Accept</ry-button>
 * </ry-card>
 * ```
 *
 * @example animated-horizontal
 * ```html
 * <ry-button animated>
 *   <span data-content="visible">Next</span>
 *   <span data-content="hidden">Sign Up</span>
 * </ry-button>
 * ```
 *
 * @example animated-vertical
 * ```html
 * <ry-button animated="vertical">
 *   <span data-content="visible">Shop</span>
 *   <span data-content="hidden"><ry-icon name="cart"></ry-icon></span>
 * </ry-button>
 * ```
 *
 * @example animated-fade
 * ```html
 * <ry-button animated="fade">
 *   <span data-content="visible">Sign in</span>
 *   <span data-content="hidden">Welcome back!</span>
 * </ry-button>
 * ```
 *
 * @example toggle
 * ```html
 * <ry-button pressed variant="primary">Bold</ry-button>
 * ```
 *
 * @tutorial click-fires-ry-click
 * ```js
 * const el = mount('<ry-button>OK</ry-button>');
 * await expectEvent(el, 'ry:click', () => el.click());
 * ```
 *
 * @tutorial loading-blocks-click
 * ```js
 * const el = mount('<ry-button loading>X</ry-button>');
 * let fired = false;
 * el.addEventListener('ry:click', () => { fired = true; });
 * el.click();
 * await expectEqual(fired, false, 'click is suppressed while loading');
 * await expectEqual(el.getAttribute('aria-busy'), 'true');
 * ```
 *
 * @tutorial programmatic-loading-toggle
 * ```js
 * const el = mount('<ry-button variant="primary">Save</ry-button>');
 * el.loading = true;
 * await expectEqual(el.getAttribute('aria-busy'), 'true');
 * await expectEqual(el.getAttribute('tabindex'), '-1');
 * el.loading = false;
 * await expectEqual(el.hasAttribute('aria-busy'), false);
 * ```
 *
 * @tutorial disabled-blocks-click
 * ```js
 * // Symmetric to loading-blocks-click but with different intent: disabled
 * // is the visual/semantic "off limits" state; loading is "busy, try again
 * // shortly". Both suppress click + keyboard activation.
 * const el = mount('<ry-button disabled>Save</ry-button>');
 * let fired = false;
 * el.addEventListener('ry:click', () => { fired = true; });
 * el.click();
 * await expectEqual(fired, false, 'click is suppressed while disabled');
 * await expectAttr(el, 'aria-disabled', 'true');
 * await expectAttr(el, 'tabindex', '-1');
 * ```
 *
 * @tutorial pressed-toggle
 * ```js
 * // `pressed` is a sticky on/off state with aria-pressed mirroring.
 * // When pressed=true, aria-pressed="true" is present.
 * // When pressed=false, both the `pressed` attribute AND aria-pressed are removed
 * // (so a non-toggle button is indistinguishable from one whose state has been cleared).
 * const el = mount('<ry-button pressed>Bold</ry-button>');
 * await expectEqual(el.pressed, true);
 * await expectAttr(el, 'aria-pressed', 'true');
 *
 * el.pressed = false;
 * await expectEqual(el.pressed, false);
 * await expectEqual(el.hasAttribute('pressed'), false, 'pressed attribute removed');
 * await expectEqual(el.hasAttribute('aria-pressed'), false, 'aria-pressed attribute also removed');
 *
 * // Round-trip back on
 * el.pressed = true;
 * await expectAttr(el, 'aria-pressed', 'true', 'toggling back on restores aria-pressed');
 * ```
 *
 * @tutorial keyboard-activates
 * ```js
 * // Space and Enter both fire ry:click. Disabled / loading suppress them too.
 * const el = mount('<ry-button>Go</ry-button>');
 *
 * await expectEvent(el, 'ry:click', () => {
 *   el.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true }));
 * });
 *
 * await expectEvent(el, 'ry:click', () => {
 *   el.dispatchEvent(new KeyboardEvent('keydown', { key: ' ', bubbles: true }));
 * });
 *
 * // Disabled blocks keyboard too
 * el.disabled = true;
 * let fired = false;
 * el.addEventListener('ry:click', () => { fired = true; });
 * el.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true }));
 * await expectEqual(fired, false, 'disabled blocks keyboard activation');
 * ```
 *
 * @tutorial icon-only-warning
 * ```js
 * // Icon-only buttons MUST have aria-label. The component warns at upgrade
 * // time. The agent should see this warning fire so it doesn't ship a11y bugs.
 * const warnings = [];
 * const origWarn = console.warn;
 * console.warn = (...args) => { warnings.push(args.join(' ')); };
 * try {
 *   mount('<ry-button icon><svg></svg></ry-button>'); // no aria-label
 * } finally {
 *   console.warn = origWarn;
 * }
 * await expectEqual(
 *   warnings.some(w => w.includes('aria-label')), true,
 *   'icon-only without aria-label emits a warning',
 * );
 *
 * // With aria-label — no warning.
 * warnings.length = 0;
 * console.warn = (...args) => { warnings.push(args.join(' ')); };
 * try {
 *   mount('<ry-button icon aria-label="Add"><svg></svg></ry-button>');
 * } finally {
 *   console.warn = origWarn;
 * }
 * await expectEqual(warnings.length, 0, 'aria-label silences the warning');
 * ```
 *
 * @chain ry-modal
 * ```js
 * // <ry-button modal="ID"> is a declarative trigger — clicking it opens the
 * // ry-modal with that ID. ry-modal's setup queries the document for any
 * // matching trigger and wires the click → open() bridge.
 * const root = mount(`
 *   <div>
 *     <ry-button modal="confirm">Open</ry-button>
 *     <ry-modal id="confirm" title="Sure?">Body</ry-modal>
 *   </div>
 * `);
 * const btn = root.querySelector('ry-button');
 * const modal = root.querySelector('ry-modal');
 * await expectEqual(modal.state, 'closed', 'modal starts closed');
 *
 * btn.click();
 * await expectEqual(modal.state, 'open', 'clicking the trigger opens the modal');
 * ```
 *
 * @chain ry-drawer
 * ```js
 * // Same pattern as ry-modal: <ry-button drawer="ID"> opens the matching drawer.
 * const root = mount(`
 *   <div>
 *     <ry-button drawer="nav">Menu</ry-button>
 *     <ry-drawer id="nav" side="left">Links</ry-drawer>
 *   </div>
 * `);
 * const btn = root.querySelector('ry-button');
 * const drawer = root.querySelector('ry-drawer');
 * await expectEqual(drawer.state, 'closed');
 * btn.click();
 * await expectEqual(drawer.state, 'open', 'clicking the trigger opens the drawer');
 * ```
 *
 * @see ry-button-group
 * @see ry-toggle-button
 * @see ry-icon
 */

import { RyElement } from '../core/ry-element.js';

export class RyButton extends RyElement {
  static get observedAttributes(): string[] {
    return ['disabled', 'pressed', 'loading', 'aria-label'];
  }

  setup(): void {
    if (!this.hasAttribute('role')) this.setAttribute('role', 'button');
    this.#applyTabIndex();
    this.#applyAriaState();
    this.#warnIfIconOnlyWithoutLabel();

    this.on(this, 'click', this.#handleClick);
    this.on(this, 'keydown', this.#handleKeydown);
  }

  attributeChangedCallback(_name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;
    if (!this.hasAttribute('data-ry-initialized')) return;
    this.#applyTabIndex();
    this.#applyAriaState();
    if (_name === 'aria-label') this.#warnIfIconOnlyWithoutLabel();
  }

  #applyTabIndex(): void {
    const inert = this.hasAttribute('disabled') || this.hasAttribute('loading');
    if (!this.hasAttribute('tabindex') || this.dataset.ryTabindexManaged) {
      this.setAttribute('tabindex', inert ? '-1' : '0');
      this.dataset.ryTabindexManaged = '1';
    }
  }

  #applyAriaState(): void {
    if (this.hasAttribute('disabled')) this.setAttribute('aria-disabled', 'true');
    else this.removeAttribute('aria-disabled');

    if (this.hasAttribute('pressed')) this.setAttribute('aria-pressed', 'true');
    else if (this.getAttribute('aria-pressed') === 'true') this.removeAttribute('aria-pressed');

    if (this.hasAttribute('loading')) this.setAttribute('aria-busy', 'true');
    else this.removeAttribute('aria-busy');
  }

  #warnIfIconOnlyWithoutLabel(): void {
    const iconOnly = this.hasAttribute('icon')
      || (this.children.length > 0
          && Array.from(this.childNodes).every(
            (n) => n.nodeType === Node.ELEMENT_NODE && (n as Element).tagName.toLowerCase() === 'ry-icon'
          ));
    if (iconOnly && !this.hasAttribute('aria-label') && !this.hasAttribute('aria-labelledby')) {
      // eslint-disable-next-line no-console
      console.warn('[ry-button] icon-only button is missing aria-label', this);
    }
  }

  #handleClick = (event: MouseEvent): void => {
    if (this.hasAttribute('disabled') || this.hasAttribute('loading')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    this.emit('click', { originalEvent: event });
  };

  #handleKeydown = (event: KeyboardEvent): void => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    if (this.hasAttribute('disabled') || this.hasAttribute('loading')) {
      event.preventDefault();
      return;
    }
    event.preventDefault();
    this.click();
  };

  // Public API ─────────────────────────────────────────────────────────
  get disabled(): boolean { return this.hasAttribute('disabled'); }
  set disabled(v: boolean) { v ? this.setAttribute('disabled', '') : this.removeAttribute('disabled'); }

  get pressed(): boolean { return this.hasAttribute('pressed'); }
  set pressed(v: boolean) { v ? this.setAttribute('pressed', '') : this.removeAttribute('pressed'); }

  get loading(): boolean { return this.hasAttribute('loading'); }
  set loading(v: boolean) { v ? this.setAttribute('loading', '') : this.removeAttribute('loading'); }
}

customElements.define('ry-button', RyButton);
